import React, { useEffect, useMemo, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import {
  ProcessParameter,
  TimelineBatch,
  Timeline,
  Recipe,
  BatchAdjustment,
} from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";
import MesDateSelector, { getDefaultMesDate, saveMesDate } from "../../../components/MES/MesDateSelector";
import BatchMaterialConfirm from "../../../components/MES/BatchMaterialConfirm";
import { formatClockTime } from "../../../utils/processParameterSerialize";
import {
  PROCESS_PHASE_GROUPS,
  buildProcessForm,
  derivedDisplayValue,
  processFieldsForGroups,
  processPayloadFromForm,
} from "./processFields";

/**
 * Red tablice odstupanja: spojeni planirani iznos iz recepture i spremljeno
 * odstupanje. `adjustment_id` postoji samo kad je red već spremljen.
 */
type AdjustmentRow = Omit<BatchAdjustment, "adjustment_id" | "ingredient_id"> & {
  adjustment_id?: number;
  ingredient_id: number;
  ingredient_name: string;
};

/** Sva procesna polja, po fazama — isti katalog koji koristi tablet nalog. */
const ALL_PROCESS_FIELDS = processFieldsForGroups(PROCESS_PHASE_GROUPS);

/**
 * Nađi procesni red zamjesa. Veže se na jedinstveni TimelineBatch; MixCode je
 * samo fallback za stare redove nastale prije te veze i nije jedinstven na
 * proizvodnom danu (isti kod se ponavlja kroz planove i poluproizvode), pa
 * nikad ne smije pobijediti eksplicitnu vezu.
 */
const findParamForBatch = (
  params: ProcessParameter[],
  batch: TimelineBatch
): ProcessParameter | undefined =>
  params.find((p) => p.TimelineBatch != null && p.TimelineBatch === batch.ID) ??
  params.find((p) => p.TimelineBatch == null && p.MixCode === batch.MixCode);

/**
 * Pocetak prve faze zamjesa. Segmenti dolaze poredani po fazi rute, pa je prvi
 * s vremenom pocetak zamjesa; stariji redovi bez segmenata imaju MixingStart.
 */
const scheduleStartIso = (batch: TimelineBatch): string | undefined =>
  batch.Phases?.find((p) => p.Start)?.Start ?? batch.MixingStart;

/** Zamjesi bez izracunatog rasporeda idu na kraj popisa. */
const scheduleStartMs = (batch: TimelineBatch): number => {
  const iso = scheduleStartIso(batch);
  const ms = iso ? new Date(iso).getTime() : NaN;
  return Number.isFinite(ms) ? ms : Number.MAX_SAFE_INTEGER;
};

/**
 * Redoslijed kojim zamjesi stvarno idu u proizvodnju. Sequence se dodjeljuje
 * grupirano po mikseru (svi zamjesi jednog miksera, pa svi drugog), pa popis
 * poredan po njemu — kako ga endpoint vraca — ne prati sat proizvodnje.
 * Sequence i ID ostaju tie-break za plan koji jos nema izracunata vremena.
 */
const byScheduleOrder = (a: TimelineBatch, b: TimelineBatch): number =>
  scheduleStartMs(a) - scheduleStartMs(b) ||
  (a.Sequence ?? 0) - (b.Sequence ?? 0) ||
  a.ID - b.ID;

/**
 * Process Data Page - Input and view process parameters
 */
const ProcessData = () => {
  savePathToLocalStorage();

  const [selectedDate, setSelectedDate] = useState<string>(getDefaultMesDate());
  const [timeline, setTimeline] = useState<Timeline | null>(null);

  const handleDateChange = (date: string) => {
    saveMesDate(date);
    setSelectedDate(date);
  };
  const [batches, setBatches] = useState<TimelineBatch[]>([]);
  const [parameters, setParameters] = useState<ProcessParameter[]>([]);
  const [selectedBatch, setSelectedBatch] = useState<TimelineBatch | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalTab, setModalTab] = useState<"params" | "materials" | "deviations">("params");
  const [batchRecipe, setBatchRecipe] = useState<Recipe | null>(null);
  const [adjustments, setAdjustments] = useState<AdjustmentRow[]>([]);
  const [loadingAdjustments, setLoadingAdjustments] = useState(false);

  /**
   * Vrijednosti forme po nazivu kolone ProcessParameter. Polja i njihove grupe
   * dolaze iz processFields.ts, pa je unos ovdje i na tabletu nužno isti set —
   * dosad su bila dva neovisna popisa i tablet je pokrivao samo dio polja.
   */
  const [form, setForm] = useState<Record<string, string>>(() => buildProcessForm({}, ALL_PROCESS_FIELDS));

  const setField = (key: string, value: string) => setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    loadDataForDate(selectedDate);
  }, [selectedDate]);

  const loadDataForDate = async (date: string) => {
    setLoading(true);
    try {
      const [timelineData, paramsData] = await Promise.all([
        mesApi.getTimelineByDate(date),
        mesApi.getProcessParameters({ production_date: date }),
      ]);
      setTimeline(timelineData);
      setBatches([...(timelineData?.TimelineBatch || [])].sort(byScheduleOrder));
      setParameters(paramsData);
    } catch (err: any) {
      toast.error("Greska pri ucitavanju: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBatch = async (batch: TimelineBatch) => {
    setSelectedBatch(batch);
    setModalTab("params");

    const existingParams = findParamForBatch(parameters, batch);
    const next = buildProcessForm(existingParams, ALL_PROCESS_FIELDS);
    // Planirana masa je koristan default za novi unos, ali ne smije prepisati
    // masu koja je već izmjerena i spremljena.
    if (!next.DoughMassKg && batch.FinalMassKg) next.DoughMassKg = String(batch.FinalMassKg);
    setForm(next);
    setShowModal(true);

    // Load recipe for deviations
    await loadRecipeForBatch(batch);
  };

  const loadRecipeForBatch = async (batch: TimelineBatch) => {
    if (!batch.RecipeID) {
      setBatchRecipe(null);
      setAdjustments([]);
      return;
    }

    setLoadingAdjustments(true);
    try {
      const [recipe, existingAdjustments] = await Promise.all([
        mesApi.getRecipe(batch.RecipeID),
        mesApi.getBatchAdjustments(batch.ID)
      ]);
      setBatchRecipe(recipe);

      // Build adjustments from recipe ingredients
      const recipeIngredients = recipe.RecipeIngredient_RecipeToRecipeIngredient_Recipe || [];
      const batchMassKg = batch.FinalMassKg || 100;

      // Scale by TOTAL dough mass, not by flour. Each ingredient mass is its baker %
      // as a fraction of the sum of ALL baker %, times the desired batch mass:
      //   factor = batchMass / Σ(all baker %) ; plannedKg = bakerPct * factor
      // (Flour is expected to sum to 100, so Σ(all %) is 100 + hydration + salt + …).
      // The previous logic summed only flour % and divided each ingredient by that,
      // over-scaling everything by ~Σ%/flour% (≈2×). This matches the tablet work
      // order (workOrderTemplate.renderRecipeTable), which was already correct.
      const sumPct = recipeIngredients.reduce((sum, ri) => sum + (ri.BakerPercentage || 0), 0);
      const scaleFactor = sumPct > 0 ? batchMassKg / sumPct : 0;

      const adjList: AdjustmentRow[] = recipeIngredients.map(ri => {
        const ingredientName = ri.Ingredient_IngredientToRecipeIngredient?.IngredientName || `Ingredient ${ri.Ingredient}`;
        const bakerPct = ri.BakerPercentage || 0;
        const plannedKg = bakerPct * scaleFactor;

        // Check if there's an existing adjustment
        const existing = existingAdjustments.find((a) => a.ingredient_id === ri.Ingredient);
        const actualKg = existing?.actual_qty_kg ?? plannedKg;
        const deviationKg = actualKg - plannedKg;
        const deviationPct = plannedKg > 0 ? (deviationKg / plannedKg) * 100 : 0;

        return {
          adjustment_id: existing?.adjustment_id,
          batch_id: batch.ID,
          ingredient_id: ri.Ingredient,
          ingredient_name: ingredientName,
          planned_qty_kg: Math.round(plannedKg * 100) / 100,
          actual_qty_kg: Math.round(actualKg * 100) / 100,
          deviation_kg: Math.round(deviationKg * 100) / 100,
          deviation_pct: Math.round(deviationPct * 10) / 10,
          reason: existing?.reason || ""
        };
      });

      setAdjustments(adjList);
    } catch (err: any) {
      console.error("Error loading recipe:", err);
      setBatchRecipe(null);
      setAdjustments([]);
    } finally {
      setLoadingAdjustments(false);
    }
  };

  const handleAdjustmentChange = (index: number, field: "actual_qty_kg" | "reason", value: string) => {
    setAdjustments(prev => {
      const updated = [...prev];
      if (field === "actual_qty_kg") {
        const actualKg = parseFloat(value) || 0;
        const plannedKg = updated[index].planned_qty_kg;
        const deviationKg = actualKg - plannedKg;
        const deviationPct = plannedKg > 0 ? (deviationKg / plannedKg) * 100 : 0;
        updated[index] = {
          ...updated[index],
          actual_qty_kg: actualKg,
          deviation_kg: Math.round(deviationKg * 100) / 100,
          deviation_pct: Math.round(deviationPct * 10) / 10
        };
      } else {
        updated[index] = { ...updated[index], reason: value };
      }
      return updated;
    });
  };

  const handleSaveAdjustments = async () => {
    if (!selectedBatch) return;

    setSaving(true);
    try {
      // Ingredients back in line with the recipe are sent too, but only when
      // they already carry a saved deviation — that is how a correction back to
      // the planned quantity clears the old entry instead of leaving it behind.
      const toSave = adjustments.filter(
        (a) => a.deviation_kg !== 0 || a.adjustment_id != null
      );

      for (const adj of toSave) {
        await mesApi.saveBatchAdjustment({
          batch_id: selectedBatch.ID,
          ingredient_id: adj.ingredient_id,
          planned_qty_kg: adj.planned_qty_kg,
          actual_qty_kg: adj.actual_qty_kg,
          deviation_kg: adj.deviation_kg,
          reason: adj.reason || null
        });
      }

      // Re-read so every row carries the id it was stored under; without it a
      // second save of the same modal cannot tell a cleared row from a new one.
      await loadRecipeForBatch(selectedBatch);
      toast.success("Odstupanja spremljena");
    } catch (err: any) {
      toast.error("Greska pri spremanju odstupanja: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveParameters = async () => {
    if (!selectedBatch) return;

    setSaving(true);
    try {
      const existingParams = findParamForBatch(parameters, selectedBatch);
      const data = {
        MixCode: selectedBatch.MixCode,
        TimelineBatch: selectedBatch.ID,
        Product: selectedBatch.ProductID,
        ProductionDate: selectedDate,
        // Sva polja (i izračunata, npr. Scrap = oblik + boja) iz istog kataloga
        // koji renderira formu — polje se ne može prikazati a ne spremiti.
        ...processPayloadFromForm(form, ALL_PROCESS_FIELDS),
      };

      if (existingParams) {
        await mesApi.updateProcessParameter(existingParams.ID, data);
        toast.success("Parametri azurirani");
      } else {
        await mesApi.createProcessParameter(data);
        toast.success("Parametri spremljeni");
      }

      setShowModal(false);
      loadDataForDate(selectedDate);
    } catch (err: any) {
      toast.error("Greska pri spremanju: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  /** Pregled parametara prati redoslijed zamjesa iz tablice iznad. */
  const orderedParameters = useMemo(() => {
    const rankOf = (param: ProcessParameter) => {
      const idx = batches.findIndex((b) =>
        param.TimelineBatch != null ? param.TimelineBatch === b.ID : param.MixCode === b.MixCode
      );
      return idx >= 0 ? idx : Number.MAX_SAFE_INTEGER;
    };
    return [...parameters].sort((a, b) => rankOf(a) - rankOf(b) || a.ID - b.ID);
  }, [parameters, batches]);

  const getBatchParamStatus = (batch: TimelineBatch) => {
    const params = findParamForBatch(parameters, batch);
    if (!params) return { hasParams: false, isComplete: false };
    const isComplete = !!(params.DoughMassKg && params.DoughTempC);
    return { hasParams: true, isComplete };
  };

  return (
    <div className="mes-page">
      <Header title="MES - Procesni parametri" />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          {/* Date Selector */}
          <MesDateSelector
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            showTimelineIndicators={true}
          />

          {/* Timeline Status */}
          {timeline && (
            <div className={`mes-status-banner ${timeline.Status}`}>
              Timeline: <strong>{timeline.Status.toUpperCase()}</strong> | {batches.length} zamjesa
            </div>
          )}

          {/* Batches List */}
          {loading ? (
            <div className="mes-section">
              <p>Ucitavanje...</p>
            </div>
          ) : batches.length > 0 ? (
            <div className="mes-section">
              <h3>Zamjesi</h3>
              <div className="mes-table-wrap">
                <table className="mes-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Start</th>
                    <th>ID Zamjesa</th>
                    <th>Proizvod</th>
                    <th>Masa (kg)</th>
                    <th>Status</th>
                    <th>Parametri</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((batch, idx) => {
                    const paramStatus = getBatchParamStatus(batch);
                    return (
                      <tr key={batch.ID} className={`batch-row status-${batch.BatchStatus}`}>
                        <td>{idx + 1}</td>
                        <td>{formatClockTime(scheduleStartIso(batch))}</td>
                        <td><code className="batch-id-code">{batch.MixCode}</code></td>
                        <td className="batch-product">{batch.ProductName}</td>
                        <td>{batch.FinalMassKg}</td>
                        <td>
                          <span className={`status-pill status-${batch.BatchStatus}`}>
                            <span className="status-dot"></span>
                            {batch.BatchStatus === "completed" ? "Zavrseno" :
                             batch.BatchStatus === "in_progress" ? "U tijeku" :
                             batch.BatchStatus === "ready" ? "Spremno" : "Planirano"}
                          </span>
                        </td>
                        <td>
                          {paramStatus.hasParams ? (
                            paramStatus.isComplete ? (
                              <span className="status-pill status-completed">
                                <span className="status-dot"></span>
                                Kompletno
                              </span>
                            ) : (
                              <span className="status-pill status-ready">
                                <span className="status-dot"></span>
                                Djelomicno
                              </span>
                            )
                          ) : (
                            <span className="status-pill status-planned">
                              <span className="status-dot"></span>
                              Nije uneseno
                            </span>
                          )}
                        </td>
                        <td>
                          <button
                            className="btn-primary"
                            onClick={() => handleSelectBatch(batch)}
                          >
                            Unesi
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          ) : (
            <div className="mes-section">
              <p className="empty-state">Nema zamjesa za odabrani datum.</p>
            </div>
          )}

          {/* Parameters Summary */}
          {parameters.length > 0 && (
            <div className="mes-section">
              <h3>Pregled unesenih parametara</h3>
              <div className="mes-table-wrap">
                <table className="mes-table">
                <thead>
                  <tr>
                    <th>ID Zamjesa</th>
                    <th>Masa (kg)</th>
                    <th>Temp (°C)</th>
                    <th>pH starter</th>
                    <th>Skart %</th>
                    <th>Napomena</th>
                  </tr>
                </thead>
                <tbody>
                  {orderedParameters.map((param) => (
                    <tr key={param.ID}>
                      <td><code className="batch-id-code">{param.MixCode}</code></td>
                      <td>{param.DoughMassKg || "-"}</td>
                      <td>{param.DoughTempC || "-"}</td>
                      <td>{param.StarterPh || "-"}</td>
                      <td>{param.ScrapPct || "-"}</td>
                      <td>{param.Notes || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Parameter Entry Modal */}
      {showModal && selectedBatch && (
        <div className="mes-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="mes-modal mes-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <div className="modal-icon">📊</div>
                <div className="modal-title-text">
                  <h3>PROCESNI PARAMETRI</h3>
                  <p className="modal-subtitle">{selectedBatch.MixCode} - {selectedBatch.ProductName}</p>
                </div>
              </div>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                ✕
              </button>
            </div>
            {/* Modal Tabs */}
            <div className="modal-tabs" style={{ display: "flex", borderBottom: "1px solid #ddd", marginBottom: "1rem" }}>
              {([
                { id: "params", label: "Parametri" },
                { id: "materials", label: "Sirovine i lotovi" },
                { id: "deviations", label: "Odstupanja" },
              ] as const).map((t) => (
                <button
                  key={t.id}
                  className={`modal-tab ${modalTab === t.id ? "active" : ""}`}
                  onClick={() => setModalTab(t.id)}
                  style={{
                    padding: "0.75rem 1.5rem",
                    border: "none",
                    background: modalTab === t.id ? "#4b9987" : "transparent",
                    color: modalTab === t.id ? "#fff" : "#666",
                    cursor: "pointer",
                    fontWeight: 500,
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
              {modalTab === "params" ? (
              <>
              {/* Polja po fazama iz processFields.ts — isti katalog koji tablet
                  nalog renderira za svoj odjel, pa se ekrani ne mogu raziti. */}
              {PROCESS_PHASE_GROUPS.map((group) => (
                <div key={group.code}>
                  <h4 style={{ marginTop: "1.5rem", marginBottom: "0.5rem", color: "#4b9987" }}>
                    {group.label}
                  </h4>
                  {group.fields.some((f) => f.type === "textarea") ? (
                    group.fields.map((field) => (
                      <div className="form-group" key={field.key}>
                        <label>{field.label}</label>
                        <textarea
                          value={form[field.key] || ""}
                          onChange={(e) => setField(field.key, e.target.value)}
                          placeholder="Dodatne napomene..."
                        />
                      </div>
                    ))
                  ) : (
                    <div className="form-grid-2">
                      {group.fields.map((field) => (
                        <div className="form-group" key={field.key}>
                          <label>{field.label}</label>
                          {field.derived ? (
                            // Izracunato iz upisanih polja — prikazuje se i sprema,
                            // ne unosi se rucno.
                            <input type="text" value={derivedDisplayValue(form, field.key)} readOnly disabled />
                          ) : (
                            <input
                              type={field.type === "textarea" ? "text" : field.type}
                              step={field.step}
                              value={form[field.key] || ""}
                              onChange={(e) => setField(field.key, e.target.value)}
                            />
                          )}
                          {field.hint && (
                            <span className="mes-hint" style={{ fontSize: "0.75rem" }}>
                              {field.hint}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              </>
              ) : modalTab === "materials" ? (
              /* Sirovine zamjesa: dodijeljeni lotovi, izmjena i potvrda utroska */
              <BatchMaterialConfirm
                batchId={selectedBatch.ID}
                onChanged={() => loadDataForDate(selectedDate)}
              />
              ) : (
              /* Deviations Tab */
              <div className="deviations-tab">
                {loadingAdjustments ? (
                  <p>Ucitavanje recepta...</p>
                ) : !batchRecipe ? (
                  <p className="empty-state">Nema recepta za ovaj zamjes.</p>
                ) : (
                  <>
                    <p style={{ marginBottom: "1rem", color: "#666" }}>
                      Recept: <strong>{batchRecipe.RecipeCode}</strong> |
                      Planirana masa: <strong>{selectedBatch?.FinalMassKg} kg</strong>
                    </p>
                    <div className="mes-table-wrap">
                      <table className="mes-table" style={{ fontSize: "0.9rem" }}>
                      <thead>
                        <tr>
                          <th>Sirovina</th>
                          <th style={{ width: "100px", textAlign: "right" }}>Plan (kg)</th>
                          <th style={{ width: "120px", textAlign: "right" }}>Stvarno (kg)</th>
                          <th style={{ width: "90px", textAlign: "right" }}>Odst. (kg)</th>
                          <th style={{ width: "70px", textAlign: "right" }}>%</th>
                          <th style={{ width: "180px" }}>Razlog</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adjustments.map((adj, idx) => (
                          <tr key={adj.ingredient_id} className={Math.abs(adj.deviation_pct) > 5 ? "row-warning" : ""}>
                            <td>{adj.ingredient_name}</td>
                            <td style={{ textAlign: "right" }}>{adj.planned_qty_kg.toFixed(2)}</td>
                            <td style={{ textAlign: "right" }}>
                              <input
                                type="number"
                                step="0.01"
                                value={adj.actual_qty_kg}
                                onChange={(e) => handleAdjustmentChange(idx, "actual_qty_kg", e.target.value)}
                                style={{ width: "100%", textAlign: "right" }}
                              />
                            </td>
                            <td style={{ textAlign: "right", color: adj.deviation_kg > 0 ? "#e74c3c" : adj.deviation_kg < 0 ? "#2980b9" : "inherit" }}>
                              {adj.deviation_kg > 0 ? "+" : ""}{adj.deviation_kg.toFixed(2)}
                            </td>
                            <td style={{ textAlign: "right", color: Math.abs(adj.deviation_pct) > 5 ? "#e74c3c" : "inherit" }}>
                              {adj.deviation_pct > 0 ? "+" : ""}{adj.deviation_pct.toFixed(1)}%
                            </td>
                            <td>
                              <input
                                type="text"
                                value={adj.reason || ""}
                                onChange={(e) => handleAdjustmentChange(idx, "reason", e.target.value)}
                                placeholder="Razlog..."
                                style={{ width: "100%" }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                    {adjustments.some(a => Math.abs(a.deviation_pct) > 5) && (
                      <p style={{ marginTop: "0.5rem", color: "#e74c3c", fontSize: "0.85rem" }}>
                        * Odstupanja veca od 5% su oznacena
                      </p>
                    )}
                  </>
                )}
              </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowModal(false)}>
                Odustani
              </button>
              {modalTab === "params" && (
                <button className="btn-submit" onClick={handleSaveParameters} disabled={saving}>
                  {saving ? "Spremanje..." : "Spremi parametre"}
                </button>
              )}
              {modalTab === "deviations" && (
                <button className="btn-submit" onClick={handleSaveAdjustments} disabled={saving || adjustments.every(a => a.deviation_kg === 0)}>
                  {saving ? "Spremanje..." : "Spremi odstupanja"}
                </button>
              )}
              {/* Sirovine se spremaju po stavci u samom panelu (svaka ima svoj
                  „Spremi" i potvrdu utroška), pa footer tu nema što spremati. */}
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        autoClose={5000}
        position="bottom-right"
        hideProgressBar={false}
        theme="colored"
      />
    </div>
  );
};

export default ProcessData;
